import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #stickyRaf = 0;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#dialog?.addEventListener('close', this.#handleDrawerClose);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.#dialog?.removeEventListener('close', this.#handleDrawerClose);
    cancelAnimationFrame(this.#stickyRaf);
    this.#resizeObserver?.disconnect();
  }

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();
    this.#observeStickyInputs();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        // Re-bind the observer to the *current* summary/content nodes (a cart change can
        // replace them) and re-measure. This covers adds into an already-open drawer, where
        // theme-drawer:open never re-fires, and keeps watching while Shopify re-renders the
        // async accelerated-checkout buttons — whose height swings the ratio across the threshold.
        const settle = () => {
          this.#observeStickyInputs();
          this.#scheduleStickyUpdate();
        };

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  /**
   * rAF-debounced re-run of the sticky calculation, so late layout changes settle to one value.
   */
  #scheduleStickyUpdate = () => {
    cancelAnimationFrame(this.#stickyRaf);
    this.#stickyRaf = requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Re-measures the sticky state whenever the summary or content changes size while the drawer
   * is open. This is what fixes the auto-open-after-add race: the accelerated checkout buttons and
   * payment terms render *after* the drawer opens, so the one-shot measurement on open under-measured
   * the summary and left the dialog stuck in the sticky (overlapping) layout. Observing until close
   * lets the ratio settle to the same value the manual-open path already produces.
   */
  #observeStickyInputs() {
    const dialog = this.#dialog;
    if (!dialog || typeof ResizeObserver === 'undefined') return;

    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(this.#scheduleStickyUpdate);

    const summary = dialog.querySelector('.cart-drawer__summary');
    const content = dialog.querySelector('.cart-drawer__content');
    if (summary) this.#resizeObserver.observe(summary);
    if (content) this.#resizeObserver.observe(content);
  }

  /** Stop observing on close so we never measure a hidden (0-height) dialog. */
  #handleDrawerClose = () => {
    cancelAnimationFrame(this.#stickyRaf);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  };

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      this.#setSticky(dialog, 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    if (!drawerHeight) return;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    this.#setSticky(dialog, ratio > this.#summaryThreshold ? 'false' : 'true');
  }

  /**
   * Writes the attribute only when it actually changes. A no-op write still can't change the observed
   * border-box sizes (position/overflow toggles don't resize the summary or the fixed-height content),
   * but skipping redundant writes removes any chance of ResizeObserver churn.
   * @param {HTMLDialogElement} dialog
   * @param {'true' | 'false'} value
   */
  #setSticky(dialog, value) {
    if (dialog.getAttribute('cart-summary-sticky') !== value) {
      dialog.setAttribute('cart-summary-sticky', value);
    }
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
